import React, { useContext, useEffect, useState } from "react";
import {
  Modal,
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
} from "react-native";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "../../../components/shared/Spinner";
import { Controller, useForm } from "react-hook-form";
import {
  getChinaShipmentsByContainerNo,
  getMergedTrackerOrders,
} from "../../../redux/dispatcher/chinaTrackerDispatcher";
import { useTranslation } from "../../../utils/useTranslation";

interface GenerateShipmentModalProps {
  visible: boolean;
  onClose: () => void;
  setSelectedEnquiryType: any;
}

const ChinaShipmentContainerSearchModal: React.FC<
  GenerateShipmentModalProps
> = ({ visible, onClose, setSelectedEnquiryType }) => {
  const { loadingChinaShipments, loading } = useSelector(
    (state: any) => state.chinaTracker.shipments,
  );
  const isChinaTrackerLoaded = useSelector(
    (state: any) => state.chinaTracker.isChinaTrackerLoaded,
  );
  const { t } = useTranslation();

  const dispatch = useDispatch<any>();
  const fetchTrackerOrders = () => {
    dispatch(getMergedTrackerOrders({ refresh: false }));
  };
  useEffect(() => {
    if (!isChinaTrackerLoaded) {
      fetchTrackerOrders();
    }
  }, [isChinaTrackerLoaded]);

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: {
      containerNo: "",
    },
  });

  const onSubmit = (formData: any) => {
    dispatch(getChinaShipmentsByContainerNo(formData.containerNo))
      .unwrap()
      .then(() => {
        // Close the search modal first, then open the form modal
        // onClose();
        // Small delay ensures the first modal is fully dismissed before opening next
        // setTimeout(() => {
        setSelectedEnquiryType("generate_shipment_form");
        // }, 300);
      })
      .catch((error: any) => {
        // Properly format the error for react-hook-form
        setError("containerNo", {
          type: "server",
          message: error || "Container not found. Please try again.",
        });
      });
  };

  const { isSmallDevice, isMediumDevice } = useContext(
    DeviceOrientationContext,
  );

  const getModalWidth = () => {
    if (isSmallDevice) return "95%";
    if (isMediumDevice) return "80%";
    return "65%";
  };

  return (
    <>
      <Modal visible={visible} transparent animationType="fade">
        <View style={styles.overlay}>
          <View style={[styles.modalContainer, { width: getModalWidth() }]}>
            <Text style={styles.title}>{t("generateShipment")}</Text>

            {loadingChinaShipments || loading || !isChinaTrackerLoaded ? (
              <Spinner
                size="small"
                color="#74BBFF"
                styles={{ flexDirection: "row", margin: "20%" }}
              />
            ) : (
              <>
                <View style={styles.inputRow}>
                  <Text style={styles.label}>{t("containerNo")}</Text>

                  <Controller
                    control={control}
                    name="containerNo"
                    rules={{ required: "Container No is required" }}
                    render={({ field: { onChange, value } }) => (
                      <TextInput
                        value={value}
                        onChangeText={onChange}
                        style={[
                          styles.input,
                          // Highlight border red when there's an error
                          errors.containerNo && styles.inputError,
                        ]}
                        placeholder={t("shipmentSearchPlaceholder")}
                        placeholderTextColor="#999"
                      />
                    )}
                  />

                  {/* Error renders dynamically as soon as errors.containerNo is set */}
                  {errors.containerNo && (
                    <Text style={styles.errorText}>
                      {errors.containerNo.message as string}
                    </Text>
                  )}
                </View>

                <View style={styles.footer}>
                  <TouchableOpacity
                    style={styles.closeButton}
                    onPress={onClose}
                  >
                    <Text style={styles.closeText}>{t("close")}</Text>
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={styles.searchButton}
                    onPress={handleSubmit(onSubmit)}
                  >
                    <Text style={styles.searchText}>{t("search")}</Text>
                  </TouchableOpacity>
                </View>
              </>
            )}
          </View>
        </View>
      </Modal>
    </>
  );
};

export default ChinaShipmentContainerSearchModal;

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.2)",
  },
  modalContainer: {
    backgroundColor: "#f5f5f5",
    borderRadius: 8,
    padding: 20,
    elevation: 5,
  },
  title: {
    fontSize: 22,
    fontWeight: "bold",
    marginBottom: 20,
  },
  inputRow: {
    backgroundColor: "#e9e9e9",
    padding: 15,
    borderRadius: 6,
    marginBottom: 25,
  },
  label: {
    fontSize: 16,
    marginBottom: 8,
    color: "#333",
  },
  input: {
    backgroundColor: "#fff",
    height: 40,
    borderRadius: 6,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: "#ddd",
  },
  inputError: {
    borderColor: "red",
    borderWidth: 1.5,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  closeButton: {
    borderWidth: 1,
    borderColor: "#999",
    paddingHorizontal: 20,
    paddingVertical: 8,
    borderRadius: 6,
    backgroundColor: "#eee",
  },
  closeText: {
    fontSize: 14,
    color: "#333",
  },
  searchButton: {
    backgroundColor: "#5aa9e6",
    paddingHorizontal: 25,
    paddingVertical: 10,
    borderRadius: 6,
  },
  searchText: {
    color: "#fff",
    fontWeight: "bold",
  },
  errorText: {
    color: "red",
    fontSize: 12,
    marginTop: 5,
  },
});
